'use client';

import { motion } from 'framer-motion';
import { Sun, Droplets, Leaf, Flame, TrendingUp, ChevronRight } from 'lucide-react';
import { InfoTooltip } from '../atomic/Tooltip';

interface EnergySourceCardMobileProps {
  title: string;
  icon: React.ReactNode;
  currentProduction: number;
  maxCapacity: number;
  co2Savings: number;
  historicData?: Array<{ year: number; value: number }>;
  description?: string;
  color: string;
  onViewDetails?: () => void;
}

const iconMap: { [key: string]: React.ReactNode } = {
  solar: <Sun className="w-5 h-5" />,
  hydro: <Droplets className="w-5 h-5" />,
  biomass: <Leaf className="w-5 h-5" />,
  biogas: <Flame className="w-5 h-5" />
};

export function EnergySourceCardMobile({
  title,
  icon,
  currentProduction,
  maxCapacity,
  co2Savings,
  historicData,
  description,
  color,
  onViewDetails
}: EnergySourceCardMobileProps) {
  const percentage = maxCapacity > 0 ? (currentProduction / maxCapacity) * 100 : 0;
  const trend = historicData && historicData.length >= 2 
    ? ((historicData[historicData.length - 1].value - historicData[0].value) / historicData[0].value) * 100
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
      onClick={onViewDetails}
    >
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${color.includes('bg-') ? color : `bg-${color}`} bg-opacity-20 flex items-center justify-center`}>
            <div className={`${color.includes('text-') ? color : color.replace('bg-', 'text-')}`}>
              {icon || iconMap[title.toLowerCase()]}
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-base text-gray-900 dark:text-white">
              {title}
            </h3>
            {trend !== 0 && (
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className={`w-3 h-3 ${trend > 0 ? 'text-green-500' : 'text-red-500 rotate-180'}`} />
                <span className={trend > 0 ? 'text-green-500' : 'text-red-500'}>
                  {Math.abs(trend).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </div>

      {/* Compact Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {currentProduction.toLocaleString('de-DE')}
          </div>
          <div className="text-xs text-gray-500">kWh/Jahr</div>
        </div>
        <div>
          <div className="text-lg font-bold text-green-600">
            {co2Savings.toLocaleString('de-DE')}
          </div>
          <div className="text-xs text-gray-500">t CO₂/Jahr</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-1">
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span>Auslastung</span>
          <span>{percentage.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`h-full rounded-full ${color}`}
          />
        </div>
      </div>
    </motion.div>
  );
}